import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { adminUpdate, adminFinancialApi, adminConfigApi } from '../../lib/adminApi';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { TabNavigation } from '../../components/admin/TabNavigation';
import { SlideOver } from '../../components/admin/SlideOver';
import { DollarSign, Euro, TrendingUp, Users, Calendar, Save, Settings, X, Coins, Download, Filter } from 'lucide-react';

interface CostStats {
  todayCost: string;
  weekCost: string;
  monthCost: string;
  todayOpenAI: string;
  todayAnthropic: string;
  todayLeonardo: string;
  todayBFL: string;
  todaySendGrid: string;
  weekOpenAI: string;
  weekAnthropic: string;
  weekLeonardo: string;
  weekBFL: string;
  weekSendGrid: string;
  monthOpenAI: string;
  monthAnthropic: string;
  monthLeonardo: string;
  monthBFL: string;
  monthSendGrid: string;
}

interface UserCost {
  user_id: string;
  first_name: string;
  company_name: string;
  email: string;
  total_cost_euro: number;
  openai_cost: number;
  anthropic_cost: number;
  leonardo_cost: number;
  bfl_cost: number;
  sendgrid_cost: number;
  total_prompts: number;
}

interface CreditLogEntry {
  id: string;
  created_at: string;
  action_type: string;
  ai_provider: string;
  credits_used: number;
  euro_cost: string;
  success: boolean;
  metadata: any;
}

interface ConfigItem {
  key: string;
  value: string;
  description: string;
}

interface CreditPricing {
  id: string;
  action_type: string;
  credits_cost: number;
  display_name: string;
  description: string | null;
  cost_basis: string;
  is_active: boolean;
  sort_order: number;
}

type TabType = 'stats' | 'users' | 'pricing' | 'credits';

export function AdminFinancial() {
  const [loading, setLoading] = useState(true);
  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserCost | null>(null);
  const [creditLogs, setCreditLogs] = useState<CreditLogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('stats');
  const [currency, setCurrency] = useState<'EUR' | 'USD'>('EUR');
  const [exchangeRate, setExchangeRate] = useState(1.0);
  const [logsPeriod, setLogsPeriod] = useState<'week' | 'month' | 'all'>('month');
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsShowing, setLogsShowing] = useState(0);

  const [costStats, setCostStats] = useState<CostStats>({
    todayCost: '0.00',
    weekCost: '0.00',
    monthCost: '0.00',
    todayOpenAI: '0.00',
    todayAnthropic: '0.00',
    todayLeonardo: '0.00',
    todayBFL: '0.00',
    todaySendGrid: '0.00',
    weekOpenAI: '0.00',
    weekAnthropic: '0.00',
    weekLeonardo: '0.00',
    weekBFL: '0.00',
    weekSendGrid: '0.00',
    monthOpenAI: '0.00',
    monthAnthropic: '0.00',
    monthLeonardo: '0.00',
    monthBFL: '0.00',
    monthSendGrid: '0.00',
  });

  const [userCosts, setUserCosts] = useState<UserCost[]>([]);
  const [periodFilter, setPeriodFilter] = useState<'today' | 'week' | 'month'>('month');

  const [pricingConfigs, setPricingConfigs] = useState<ConfigItem[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const [creditPricing, setCreditPricing] = useState<CreditPricing[]>([]);
  const [creditEdits, setCreditEdits] = useState<Record<string, number>>({});

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    if (periodFilter) {
      loadUserCosts();
    }
  }, [periodFilter]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadCostStats(),
      loadUserCosts(),
      loadPricingConfigs(),
      loadCreditPricing(),
    ]);
    setLoading(false);
  };

  const loadCostStats = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const periods = ['today', 'week', 'month'];
      const costsData = await Promise.all(
        periods.map(async (period) => {
          try {
            return await adminFinancialApi.costStats(period);
          } catch (error) {
            return { totalCost: '0.00', openaiCost: '0.00', anthropicCost: '0.00', leonardoCost: '0.00', bflCost: '0.00', sendgridCost: '0.00' };
          }
        })
      );

      setCostStats({
        todayCost: costsData[0].totalCost,
        weekCost: costsData[1].totalCost,
        monthCost: costsData[2].totalCost,
        todayOpenAI: costsData[0].openaiCost,
        todayAnthropic: costsData[0].anthropicCost ?? '0.00',
        todayLeonardo: costsData[0].leonardoCost,
        todayBFL: costsData[0].bflCost ?? '0.00',
        todaySendGrid: costsData[0].sendgridCost,
        weekOpenAI: costsData[1].openaiCost,
        weekAnthropic: costsData[1].anthropicCost ?? '0.00',
        weekLeonardo: costsData[1].leonardoCost,
        weekBFL: costsData[1].bflCost ?? '0.00',
        weekSendGrid: costsData[1].sendgridCost,
        monthOpenAI: costsData[2].openaiCost,
        monthAnthropic: costsData[2].anthropicCost ?? '0.00',
        monthLeonardo: costsData[2].leonardoCost,
        monthBFL: costsData[2].bflCost ?? '0.00',
        monthSendGrid: costsData[2].sendgridCost,
      });
    } catch (error) {
      console.error('Failed to load cost stats:', error);
    }
  };

  const loadUserCosts = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const result = await adminFinancialApi.userCosts(periodFilter);
      setUserCosts(result.data || []);
    } catch (error) {
      console.error('Failed to load user costs:', error);
    }
  };

  const loadPricingConfigs = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const result = await adminConfigApi.systemConfig();
      const data = result.data || [];
      const pricing = data.filter((c: ConfigItem) =>
        !(c.description && c.description.includes('[DEPRECATED')) &&
        (c.key.includes('price') || c.key.includes('exchange_rate') || c.key.includes('leonardo_cost') || c.key.includes('sendgrid_cost'))
      );
      setPricingConfigs(pricing);

      const initialFormData: Record<string, string> = {};
      pricing.forEach((config: ConfigItem) => {
        initialFormData[config.key] = config.value;
        if (config.key === 'exchange_rate_usd_to_euro') {
          setExchangeRate(parseFloat(config.value) || 1.0);
        }
      });
      setFormData(initialFormData);
    } catch (error) {
      console.error('Error loading pricing configs:', error);
    }
  };

  const loadCreditPricing = async () => {
    try {
      const { data, error } = await supabase
        .from('credit_pricing')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      if (data) {
        setCreditPricing(data);
        const edits: Record<string, number> = {};
        data.forEach((item) => {
          edits[item.action_type] = item.credits_cost;
        });
        setCreditEdits(edits);
      }
    } catch (error) {
      console.error('Error loading credit pricing:', error);
    }
  };

  const handleSavePricing = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveMessage('');

    try {
      for (const config of pricingConfigs) {
        const newValue = formData[config.key];
        if (newValue !== config.value) {
          await adminUpdate({
            table: 'system_config',
            operation: 'update',
            data: {
              value: newValue,
              updated_at: new Date().toISOString(),
            },
            filters: { key: config.key },
          });
        }
      }

      setSaveMessage('Prijzen succesvol opgeslagen!');
      await loadPricingConfigs();

      setTimeout(() => {
        setSaveMessage('');
      }, 3000);
    } catch (error: any) {
      setSaveMessage('Fout bij opslaan: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCreditPricing = async () => {
    setSaving(true);
    try {
      for (const item of creditPricing) {
        const newCost = creditEdits[item.action_type];
        if (newCost !== item.credits_cost) {
          await adminUpdate({
            table: 'credit_pricing',
            operation: 'update',
            id: item.id,
            data: {
              credits_cost: newCost,
              updated_at: new Date().toISOString(),
            },
          });
        }
      }

      setSaveMessage('Credit prijzen opgeslagen');
      await loadCreditPricing();
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      alert(`Fout bij opslaan: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const convertCost = (euroCost: string | number | null | undefined): string => {
    if (euroCost === null || euroCost === undefined || euroCost === '') {
      return '0.0000';
    }

    const cost = typeof euroCost === 'string' ? parseFloat(euroCost) : euroCost;
    if (isNaN(cost) || !isFinite(cost)) return '0.0000';

    if (currency === 'USD') {
      return (cost / exchangeRate).toFixed(4);
    }
    return cost.toFixed(4);
  };

  const currencySymbol = currency === 'EUR' ? '€' : '$';

  const handleUserClick = async (user: UserCost) => {
    setSelectedUser(user);
    setSlideOverOpen(true);
    setLogsPeriod('month');
    loadUserCreditLogs(user.user_id, 'month');
  };

  const loadUserCreditLogs = async (userId: string, period: 'week' | 'month' | 'all') => {
    setLogsLoading(true);
    setCreditLogs([]);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-financial?action=user-credits&userId=${userId}&period=${period}&limit=500`;
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setCreditLogs(result.data || []);
        setLogsTotal(result.total || 0);
        setLogsShowing(result.showing || 0);
      }
    } catch (error) {
      console.error('Failed to load credit logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!selectedUser || creditLogs.length === 0) return;

    const csvHeaders = ['Datum', 'Actie', 'Provider', 'Credits', 'Kosten (EUR)', 'Status'];
    const csvRows = creditLogs.map(log => [
      new Date(log.created_at).toLocaleString('nl-NL'),
      log.action_type,
      log.ai_provider || '-',
      (log.credits_used || 0).toFixed(2),
      log.euro_cost,
      log.success ? 'Success' : 'Failed'
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `credit-log-${selectedUser.first_name}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getProviderColor = (provider: string) => {
    switch (provider) {
      case 'OpenAI':
        return 'bg-blue-100 text-blue-800';
      case 'Anthropic':
        return 'bg-purple-100 text-purple-800';
      case 'Leonardo':
        return 'bg-green-100 text-green-800';
      case 'BFL':
        return 'bg-indigo-100 text-indigo-800';
      case 'SendGrid':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateProviderTotals = (logs: CreditLogEntry[]) => {
    const totals = {
      OpenAI: { credits: 0, cost: 0 },
      Anthropic: { credits: 0, cost: 0 },
      Leonardo: { credits: 0, cost: 0 },
      BFL: { credits: 0, cost: 0 },
      SendGrid: { credits: 0, cost: 0 },
    };

    logs.forEach(log => {
      if (totals[log.ai_provider as keyof typeof totals]) {
        totals[log.ai_provider as keyof typeof totals].credits += log.credits_used || 0;
        totals[log.ai_provider as keyof typeof totals].cost += parseFloat(log.euro_cost) || 0;
      }
    });

    return totals;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-900 mb-2">Financieel Overzicht</h1>
              <p className="text-gray-600">Kosten, prijzen en gebruikersstatistieken</p>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm border border-gray-200">
              <button
                onClick={() => setCurrency('EUR')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  currency === 'EUR'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Euro className="w-4 h-4" />
                EUR
              </button>
              <button
                onClick={() => setCurrency('USD')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  currency === 'USD'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <DollarSign className="w-4 h-4" />
                USD
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6 pb-0">
            <TabNavigation
              tabs={[
                { id: 'stats', label: 'Kosten Statistieken', icon: TrendingUp },
                { id: 'users', label: 'Kosten per Gebruiker', icon: Users },
                { id: 'pricing', label: 'API Prijzen', icon: Settings },
                { id: 'credits', label: 'Credit Kosten', icon: Coins },
              ]}
              activeTab={activeTab}
              onTabChange={(tab) => setActiveTab(tab as TabType)}
            />
          </div>
        </div>

        {activeTab === 'stats' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 hover:shadow-md transition-shadow">
                <div className="text-sm font-semibold text-blue-700 mb-2 uppercase tracking-wide">Vandaag</div>
                <div className="text-3xl font-bold text-blue-900 mb-4">{currencySymbol}{convertCost(costStats.todayCost)}</div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-700">Anthropic</span>
                    <span className="font-semibold text-blue-900">{currencySymbol}{convertCost(costStats.todayAnthropic)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-700">OpenAI</span>
                    <span className="font-semibold text-blue-900">{currencySymbol}{convertCost(costStats.todayOpenAI)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-700">BFL (Flux)</span>
                    <span className="font-semibold text-blue-900">{currencySymbol}{convertCost(costStats.todayBFL)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-700">Leonardo</span>
                    <span className="font-semibold text-blue-900">{currencySymbol}{convertCost(costStats.todayLeonardo)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-700">SendGrid</span>
                    <span className="font-semibold text-blue-900">{currencySymbol}{convertCost(costStats.todaySendGrid)}</span>
                  </div>
                </div>
              </div>
              <div className="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 hover:shadow-md transition-shadow">
                <div className="text-sm font-semibold text-green-700 mb-2 uppercase tracking-wide">Laatste 7 dagen</div>
                <div className="text-3xl font-bold text-green-900 mb-4">{currencySymbol}{convertCost(costStats.weekCost)}</div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-green-700">Anthropic</span>
                    <span className="font-semibold text-green-900">{currencySymbol}{convertCost(costStats.weekAnthropic)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-green-700">OpenAI</span>
                    <span className="font-semibold text-green-900">{currencySymbol}{convertCost(costStats.weekOpenAI)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-green-700">BFL (Flux)</span>
                    <span className="font-semibold text-green-900">{currencySymbol}{convertCost(costStats.weekBFL)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-green-700">Leonardo</span>
                    <span className="font-semibold text-green-900">{currencySymbol}{convertCost(costStats.weekLeonardo)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-green-700">SendGrid</span>
                    <span className="font-semibold text-green-900">{currencySymbol}{convertCost(costStats.weekSendGrid)}</span>
                  </div>
                </div>
              </div>
              <div className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200 hover:shadow-md transition-shadow">
                <div className="text-sm font-semibold text-orange-700 mb-2 uppercase tracking-wide">Laatste 30 dagen</div>
                <div className="text-3xl font-bold text-orange-900 mb-4">{currencySymbol}{convertCost(costStats.monthCost)}</div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-orange-700">Anthropic</span>
                    <span className="font-semibold text-orange-900">{currencySymbol}{convertCost(costStats.monthAnthropic)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-orange-700">OpenAI</span>
                    <span className="font-semibold text-orange-900">{currencySymbol}{convertCost(costStats.monthOpenAI)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-orange-700">BFL (Flux)</span>
                    <span className="font-semibold text-orange-900">{currencySymbol}{convertCost(costStats.monthBFL)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-orange-700">Leonardo</span>
                    <span className="font-semibold text-orange-900">{currencySymbol}{convertCost(costStats.monthLeonardo)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-orange-700">SendGrid</span>
                    <span className="font-semibold text-orange-900">{currencySymbol}{convertCost(costStats.monthSendGrid)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Kosten per Gebruiker
              </h3>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-600" />
                <select
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value as 'today' | 'week' | 'month')}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                >
                  <option value="today">Vandaag</option>
                  <option value="week">Laatste 7 dagen</option>
                  <option value="month">Laatste 30 dagen</option>
                </select>
              </div>
            </div>

            {userCosts.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-lg font-semibold text-gray-900 mb-1">Geen kosten gevonden</p>
                <p className="text-sm text-gray-600">Probeer een andere periode te selecteren</p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Gebruiker</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Bedrijf</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Prompts</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Anthropic</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">OpenAI</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">BFL</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Leonardo</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">SendGrid</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Totaal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userCosts.map((user, index) => (
                        <tr
                          key={user.user_id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleUserClick(user);
                          }}
                          className={`border-b border-gray-100 hover:bg-blue-50 transition-all cursor-pointer group ${
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                          }`}
                        >
                          <td className="py-4 px-4">
                            <div className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">{user.first_name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{user.email}</div>
                          </td>
                          <td className="py-4 px-4 text-gray-700">{user.company_name}</td>
                          <td className="py-4 px-4 text-right font-medium text-gray-900">{user.total_prompts}</td>
                          <td className="py-4 px-4 text-right text-gray-700">
                            {currencySymbol}{convertCost(user.anthropic_cost ?? 0)}
                          </td>
                          <td className="py-4 px-4 text-right text-gray-700">
                            {currencySymbol}{convertCost(user.openai_cost)}
                          </td>
                          <td className="py-4 px-4 text-right text-gray-700">
                            {currencySymbol}{convertCost(user.bfl_cost ?? 0)}
                          </td>
                          <td className="py-4 px-4 text-right text-gray-700">
                            {currencySymbol}{convertCost(user.leonardo_cost)}
                          </td>
                          <td className="py-4 px-4 text-right text-gray-700">
                            {currencySymbol}{convertCost(user.sendgrid_cost)}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <span className="font-bold text-gray-900">
                              {currencySymbol}{convertCost(user.total_cost_euro)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-300 font-bold bg-gradient-to-r from-gray-50 to-gray-100">
                        <td className="py-4 px-4 text-gray-900" colSpan={2}>Totaal</td>
                        <td className="py-4 px-4 text-right text-gray-900">
                          {userCosts.reduce((sum, u) => sum + u.total_prompts, 0)}
                        </td>
                        <td className="py-4 px-4 text-right text-gray-900">
                          {currencySymbol}{convertCost(userCosts.reduce((sum, u) => sum + (u.anthropic_cost ?? 0), 0))}
                        </td>
                        <td className="py-4 px-4 text-right text-gray-900">
                          {currencySymbol}{convertCost(userCosts.reduce((sum, u) => sum + u.openai_cost, 0))}
                        </td>
                        <td className="py-4 px-4 text-right text-gray-900">
                          {currencySymbol}{convertCost(userCosts.reduce((sum, u) => sum + (u.bfl_cost ?? 0), 0))}
                        </td>
                        <td className="py-4 px-4 text-right text-gray-900">
                          {currencySymbol}{convertCost(userCosts.reduce((sum, u) => sum + u.leonardo_cost, 0))}
                        </td>
                        <td className="py-4 px-4 text-right text-gray-900">
                          {currencySymbol}{convertCost(userCosts.reduce((sum, u) => sum + u.sendgrid_cost, 0))}
                        </td>
                        <td className="py-4 px-4 text-right text-gray-900">
                          {currencySymbol}{convertCost(userCosts.reduce((sum, u) => sum + u.total_cost_euro, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'pricing' && (
          <form onSubmit={handleSavePricing} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="pb-6 border-b border-gray-200 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2 mb-2">
                <Settings className="w-5 h-5" />
                API Prijzen & Wisselkoers
              </h2>
              <p className="text-sm text-gray-600">
                Beheer prijzen en wisselkoers voor kostenberekening. Alle API-prijzen worden opgeslagen in USD en omgerekend naar EUR met de centrale wisselkoers.
              </p>
            </div>

            <div className="space-y-6">
              {pricingConfigs.map((config) => (
                <div key={config.key} className="pb-6 border-b border-gray-200 last:border-0 last:pb-0">
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      {config.key}
                    </label>
                    <p className="text-sm text-gray-600">{config.description}</p>
                  </div>

                  <input
                    type="number"
                    step="0.000001"
                    min="0"
                    value={formData[config.key] || ''}
                    onChange={(e) => {
                      const newFormData = { ...formData, [config.key]: e.target.value };
                      setFormData(newFormData);
                      if (config.key === 'exchange_rate_usd_to_euro') {
                        setExchangeRate(parseFloat(e.target.value) || 1.0);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    placeholder={config.value}
                  />
                </div>
              ))}
            </div>

            {saveMessage && (
              <div className={`mt-6 p-4 rounded-lg ${
                saveMessage.includes('Fout')
                  ? 'bg-red-50 border border-red-200 text-red-700'
                  : 'bg-green-50 border border-green-200 text-green-700'
              }`}>
                <p className="text-sm font-medium">{saveMessage}</p>
              </div>
            )}

            <div className="pt-6 mt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={saving}
                className="w-full px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Bezig met opslaan...' : 'Prijzen Opslaan'}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'credits' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="pb-6 border-b border-gray-200 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2 mb-2">
                <Coins className="w-5 h-5" />
                Credit Kosten
              </h2>
              <p className="text-sm text-gray-600">
                Beheer hoeveel credits elke actie kost voor gebruikers.
              </p>
            </div>

            <div className="space-y-6">
              {creditPricing.map((item) => (
                <div key={item.id} className="pb-6 border-b border-gray-200 last:border-0 last:pb-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        {item.display_name}
                      </label>
                      <p className="text-sm text-gray-600">{item.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Basis: {item.cost_basis.replace('_', ' ')}
                      </p>
                    </div>
                    <div className="w-32 ml-4">
                      <input
                        type="number"
                        min="0"
                        value={creditEdits[item.action_type] || 0}
                        onChange={(e) => setCreditEdits({
                          ...creditEdits,
                          [item.action_type]: parseInt(e.target.value) || 0
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm text-right"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {saveMessage && (
              <div className={`mt-6 p-4 rounded-lg ${
                saveMessage.includes('Fout')
                  ? 'bg-red-50 border border-red-200 text-red-700'
                  : 'bg-green-50 border border-green-200 text-green-700'
              }`}>
                <p className="text-sm font-medium">{saveMessage}</p>
              </div>
            )}

            <div className="pt-6 mt-6 border-t border-gray-200">
              <button
                onClick={handleSaveCreditPricing}
                disabled={saving}
                className="w-full px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Bezig met opslaan...' : 'Credit Kosten Opslaan'}
              </button>
            </div>
          </div>
        )}
      </div>

      <SlideOver
        isOpen={slideOverOpen}
        onClose={() => {
          setSlideOverOpen(false);
          setSelectedUser(null);
          setCreditLogs([]);
        }}
        title={`Credit Log - ${selectedUser?.first_name || ''}`}
        width="wide"
      >
        {selectedUser && (
          <div className="p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedUser.first_name}</h3>
                  <p className="text-sm text-gray-600">{selectedUser.email}</p>
                  {selectedUser.company_name && (
                    <p className="text-sm text-gray-600">{selectedUser.company_name}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportToCSV}
                    disabled={creditLogs.length === 0}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <Filter className="w-4 h-4 text-gray-600" />
                <select
                  value={logsPeriod}
                  onChange={(e) => {
                    const period = e.target.value as 'week' | 'month' | 'all';
                    setLogsPeriod(period);
                    loadUserCreditLogs(selectedUser.user_id, period);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="week">Laatste 7 dagen</option>
                  <option value="month">Laatste 30 dagen</option>
                  <option value="all">Alle tijd</option>
                </select>
                {logsTotal > logsShowing && (
                  <div className="text-sm text-gray-600">
                    Toon eerste <span className="font-semibold">{logsShowing}</span> van <span className="font-semibold">{logsTotal}</span> logs
                  </div>
                )}
              </div>

              {logsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {(() => {
                      const totals = calculateProviderTotals(creditLogs);
                      return (
                        <>
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200 hover:shadow-md transition-shadow">
                            <div className="text-xs font-semibold text-blue-700 mb-2 uppercase tracking-wide">OpenAI</div>
                            <div className="text-lg font-bold text-blue-900">{(totals.OpenAI.credits || 0).toFixed(0)} credits</div>
                            <div className="text-sm font-semibold text-blue-700 mt-1">{currencySymbol}{convertCost(totals.OpenAI.cost || 0)}</div>
                          </div>
                          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200 hover:shadow-md transition-shadow">
                            <div className="text-xs font-semibold text-green-700 mb-2 uppercase tracking-wide">Leonardo</div>
                            <div className="text-lg font-bold text-green-900">{(totals.Leonardo.credits || 0).toFixed(0)} credits</div>
                            <div className="text-sm font-semibold text-green-700 mt-1">{currencySymbol}{convertCost(totals.Leonardo.cost || 0)}</div>
                          </div>
                          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200 hover:shadow-md transition-shadow">
                            <div className="text-xs font-semibold text-orange-700 mb-2 uppercase tracking-wide">SendGrid</div>
                            <div className="text-lg font-bold text-orange-900">{(totals.SendGrid.credits || 0).toFixed(0)} credits</div>
                            <div className="text-sm font-semibold text-orange-700 mt-1">{currencySymbol}{convertCost(totals.SendGrid.cost || 0)}</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 sticky top-0 border-b border-gray-200">
                          <tr>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Datum</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Actie</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Provider</th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Credits</th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Kosten</th>
                            <th className="text-center py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {creditLogs.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="py-12 text-center">
                                <div className="text-gray-500">
                                  <p className="text-sm font-medium">Geen credit log entries gevonden</p>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            creditLogs.map((log, index) => (
                              <tr key={log.id} className={`border-b border-gray-100 hover:bg-blue-50 transition-colors ${
                                index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                              }`}>
                                <td className="py-3 px-4 text-sm text-gray-700">
                                  {formatDate(log.created_at)}
                                </td>
                                <td className="py-3 px-4 text-sm font-medium text-gray-900">
                                  {log.action_type}
                                </td>
                                <td className="py-3 px-4">
                                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getProviderColor(log.ai_provider)}`}>
                                    {log.ai_provider}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-right text-sm font-semibold text-gray-900">
                                  {(log.credits_used || 0).toFixed(2)}
                                </td>
                                <td className="py-3 px-4 text-right text-sm font-semibold text-gray-900">
                                  {currencySymbol}{convertCost(log.euro_cost)}
                                </td>
                                <td className="py-3 px-4 text-center">
                                  {log.success ? (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                      Success
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                      Failed
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </SlideOver>
    </AdminLayout>
  );
}
